/**
 * Placement map — where each sellable placement sits on the real
 * lamptwist.com pages.
 *
 * Images: frontend/public/placement-map/{home,collection,product}.jpg,
 * desktop captures at 1440px layout width (resized to 1200px). To refresh
 * a page, replace the file and keep the aspect ratio roughly the same —
 * hotspots are percentages of the image, so small layout shifts survive.
 *
 * Captures and hotspot coordinates come from the same headless render
 * (2026-10-07, scratch script via the DevTools protocol: section bounding
 * boxes at 1440px, English storefront, cookie banner blocked). `rate` is the rate-card key the
 * spot sells; several spots may share a key (two product carousels).
 */
export interface Hotspot {
  /** PlacementRate.key */
  rate: string;
  /** Short label for the pin. */
  label: string;
  /** Percent of image width/height. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Shown when the slot is not visible on the capture (appears when booked). */
  ghost?: boolean;
}

export interface MapPage {
  key: 'home' | 'collection' | 'product';
  title: string;
  src: string;
  /** Natural image size, for the aspect ratio before the image loads. */
  width: number;
  height: number;
  spots: Hotspot[];
  /** Placements sold on this page that have no fixed spot. */
  unplaced?: { rate: string; label: string; note: string }[];
}

// Measured at 1440 layout width; converted to % below.
const W = 1440;
const px = (page: { H: number }, top: number, h: number, left = 0, w = W): Pick<Hotspot, 'x' | 'y' | 'w' | 'h'> => ({
  x: +(left / W * 100).toFixed(2),
  y: +(top / page.H * 100).toFixed(2),
  w: +(w / W * 100).toFixed(2),
  h: +(h / page.H * 100).toFixed(2),
});

const HOME = { H: 9610 };
const COLLECTION = { H: 5130 };
const PRODUCT = { H: 7756 };

export const MAP_PAGES: MapPage[] = [
  {
    key: 'home',
    title: 'Homepage',
    src: '/placement-map/home.jpg',
    width: 1200,
    height: 8008,
    spots: [
      { rate: 'navigation-bar', label: 'Navigation bar', ...px(HOME, 0, 44) },
      { rate: 'hero-100', label: 'Hero · full width', ...px(HOME, 44, 660) },
      { rate: 'hero-50', label: 'Hero · half width', ...px(HOME, 44, 660, 0, 720) },
      { rate: 'product-grid', label: 'Product grid', ...px(HOME, 704, 1150) },
      { rate: 'product-carousel', label: 'Product carousel', ...px(HOME, 1854, 754) },
      { rate: 'product-carousel', label: 'Product carousel', ...px(HOME, 2986, 778) },
      { rate: 'browse-by-room', label: 'Browse by room', ...px(HOME, 3764, 774) },
      { rate: 'brand-logo-carousel', label: 'Brand logo carousel', ...px(HOME, 6197, 378) },
    ],
    unplaced: [
      { rate: 'promo-banner', label: 'Promotional banner', note: 'Full-width banner slotted between two homepage sections when booked.' },
    ],
  },
  {
    key: 'collection',
    title: 'Collection page',
    src: '/placement-map/collection.jpg',
    width: 1200,
    height: 4275,
    spots: [
      { rate: 'navigation-bar', label: 'Navigation bar', ...px(COLLECTION, 0, 44) },
      { rate: 'collection-page', label: 'Collection header', ...px(COLLECTION, 116, 340) },
      { rate: 'collection-featured', label: 'Featured products', ...px(COLLECTION, 456, 380, 390, 1004), ghost: true },
    ],
  },
  {
    key: 'product',
    title: 'Product page',
    src: '/placement-map/product.jpg',
    width: 1200,
    height: 6463,
    spots: [
      { rate: 'navigation-bar', label: 'Navigation bar', ...px(PRODUCT, 0, 44) },
      { rate: 'pdp-banner', label: 'Banner on product pages', ...px(PRODUCT, 1959, 240), ghost: true },
    ],
  },
];

/** Partnership-page feature / add-on ids → rate-card keys (for highlighting). */
export const RATE_BY_PARTNERSHIP_ITEM: Record<string, string> = {
  'homepage-banner': 'hero-100',
  'homepage-product-grid': 'product-grid',
  'homepage-brand-carousel': 'brand-logo-carousel',
  'collection-page': 'collection-page',
  'collection-featured-top': 'collection-featured',
  'campaign-discount': 'pdp-banner',
};

export const pagesWithAny = (keys: Set<string>) =>
  MAP_PAGES.filter((p) => p.spots.some((s) => keys.has(s.rate)) || (p.unplaced ?? []).some((u) => keys.has(u.rate)));
